import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

export type ExpoStatus = "Planned" | "Approved" | "Open" | "Active" | "Closing" | "Closed" | "Archived" | "Cancelled";
export type ExpoTeamRole = "Manager" | "Seller" | "Cashier" | "StockCustodian" | "FinanceReviewer" | "Viewer";

export interface ExpoTeamMember { id: string; userId: string; userName: string; teamRole: ExpoTeamRole; effectiveFrom: string; effectiveTo?: string | null; isPrimary: boolean; }
export interface ExpoStatusHistory { id: string; fromStatus: ExpoStatus; toStatus: ExpoStatus; reason?: string | null; changedByUserId: string; changedByUserName: string; changedAt: string; }
export interface Expo {
  id: string; code: string; name: string; locationName: string; address?: string | null; county?: string | null;
  town?: string | null; description?: string | null; startDate: string; endDate: string; timezone: string;
  status: ExpoStatus; managerUserId: string; managerName: string; creditSalesAllowed: boolean;
  creditApprovalThreshold?: number | null; defaultCreditDays?: number | null; rowVersion: number;
  createdAt: string; updatedAt?: string | null; teamMembers: ExpoTeamMember[]; statusHistory: ExpoStatusHistory[];
}

export interface ExpoCloseReadiness {
  expoId: string;
  status: ExpoStatus;
  canStartClosing: boolean;
  canClose: boolean;
  unsoldStockQuantity: number;
  outstandingReceivable: number;
  openTransferCount: number;
  pendingPaymentCount: number;
  unreconciledPaymentCount: number;
  blockers: { code: string; message: string; quantity?: number | null; amount?: number | null }[];
}

export interface ExpoInput {
  name: string; locationName: string; address?: string | null; county?: string | null; town?: string | null;
  description?: string | null; startDate: string; endDate: string; timezone?: string;
  managerUserId: string; creditSalesAllowed: boolean; creditApprovalThreshold?: number | null;
  defaultCreditDays?: number | null;
}

export const exposApi = {
  list: async (params?: { search?: string; status?: string; from?: string; to?: string }) =>
    (await apiClient.get<ApiResponse<Expo[]>>("/expos", { params })).data.data ?? [],
  get: async (id: string) => (await apiClient.get<ApiResponse<Expo>>(`/expos/${id}`)).data.data,
  closeReadiness: async (id: string) =>
    (await apiClient.get<ApiResponse<ExpoCloseReadiness>>(`/expos/${id}/close-readiness`)).data.data,
  create: async (payload: ExpoInput) => (await apiClient.post<ApiResponse<Expo>>("/expos", payload)).data.data,
  update: async (id: string, payload: ExpoInput & { rowVersion: number }) =>
    (await apiClient.put<ApiResponse<Expo>>(`/expos/${id}`, payload)).data.data,
  changeStatus: async (id: string, action: string, rowVersion: number, reason?: string) =>
    (await apiClient.post<ApiResponse<Expo>>(`/expos/${id}/${action}`, { rowVersion, reason: reason || null })).data.data,
  addTeamMember: async (id: string, payload: { userId: string; teamRole: ExpoTeamRole; effectiveFrom: string; effectiveTo?: string | null; isPrimary: boolean }) =>
    (await apiClient.post<ApiResponse<Expo>>(`/expos/${id}/team`, payload)).data.data,
  removeTeamMember: async (id: string, memberId: string) =>
    (await apiClient.delete<ApiResponse<Expo>>(`/expos/${id}/team/${memberId}`)).data.data,
};
